import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  CAlert, CBadge, CButton, CCard, CCol, CFormCheck, CFormInput, CFormSelect, CNav, CNavItem, CNavLink,
  CPagination, CPaginationItem, CRow, CSpinner, CTable, CTableBody, CTableDataCell, CTableHead,
  CTableHeaderCell, CTableRow,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { PriorityBadge, StateBadge } from '../components/StateBadge';
import { formatDate } from '../format';

type Page = Schemas['ContractListItemResponsePagedResponse'];

const PAGE_SIZE = 25;

const sortOptions = [
  ['', 'Naposledy změněné'],
  ['dueDate:asc', 'Termín — nejbližší'],
  ['priority:desc', 'Priorita'],
  ['createdAt:desc', 'Nejnovější'],
  ['contractNumber:desc', 'Číslo zakázky'],
] as const;

/**
 * Seznam zakázek. Filtry žijí v URL — odkaz na „blokované po termínu" se dá poslat kolegovi
 * a funguje i tlačítko zpět.
 * <p>
 * Výchozí je jen to, na čem se pracuje; hotové a zrušené jsou v archivu (ADR-016).
 */
export function ContractsPage() {
  const [params, setParams] = useSearchParams();
  // Vysledek si pamatuje, pro jake filtry plati. Spinner se z toho odvodi (bez mazani stavu
  // v efektu) a pomala odpoved na stare filtry neprepise novou.
  const [loaded, setLoaded] = useState<{ key: string; page: Page } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const archived = params.get('archiv') === '1';
  // V archivu nedavaji smysl - hotova zakazka neni po terminu ani blokovana. Kdyby se
  // posilaly dal, archiv by vysel prazdny a nebylo by videt proc.
  const overdue = !archived && params.get('poTerminu') === '1';
  const blocked = !archived && params.get('blokovane') === '1';
  const search = params.get('hledat') ?? '';
  const sort = params.get('razeni') ?? '';
  const page = Number(params.get('strana') ?? '1') || 1;

  const update = useCallback((changes: Record<string, string | null>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  }, [setParams]);

  // Hledani se posila az po kratke pauze, ne pri kazdem pismenu.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput !== search) update({ hledat: searchInput || null, strana: null });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, search, update]);

  const queryKey = JSON.stringify([archived, overdue, blocked, search, sort, page]);
  const result = loaded?.key === queryKey ? loaded.page : null;

  useEffect(() => {
    // Odpoved na uz neplatne filtry se zahodi. Jinak by pozdni odpoved na starsi hledani
    // prepsala novou a seznam by zustal "nacitat" (klic by nesedel na aktualni filtry).
    let current = true;
    api.GET('/api/v1/contracts', {
      params: {
        query: {
          archived,
          overdue: overdue || undefined,
          blocked: blocked || undefined,
          search: search || undefined,
          sort: sort || undefined,
          page,
          pageSize: PAGE_SIZE,
        },
      },
    }).then(({ data, error: problem }) => {
      if (!current) return;
      if (data) {
        setLoaded({ key: queryKey, page: data });
      } else {
        setError(problemMessage(problem));
      }
    });
    return () => { current = false; };
  }, [archived, overdue, blocked, search, sort, page, queryKey]);

  const pages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Zakázky</h2>
        <CButton color="primary" as={Link} to="/zakazky/nova">Nová zakázka</CButton>
      </div>

      <CNav variant="underline-border" className="mb-3">
        <CNavItem>
          <CNavLink active={!archived} as="button" onClick={() => update({ archiv: null, strana: null })}>
            Rozpracované
          </CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink active={archived} as="button" onClick={() => update({ archiv: '1', poTerminu: null, blokovane: null, strana: null })}>
            Archiv
          </CNavLink>
        </CNavItem>
      </CNav>

      <CRow className="g-3 align-items-center mb-3">
        <CCol md={5}>
          <CFormInput
            placeholder="Hledat číslo, název nebo zákazníka…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </CCol>
        <CCol md="auto">
          <CFormSelect value={sort} onChange={(e) => update({ razeni: e.target.value, strana: null })}>
            {sortOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </CFormSelect>
        </CCol>
        {!archived && (
          <>
            <CCol xs="auto">
              <CFormCheck id="overdue" label="Po termínu" checked={overdue}
                onChange={(e) => update({ poTerminu: e.target.checked ? '1' : null, strana: null })} />
            </CCol>
            <CCol xs="auto">
              <CFormCheck id="blocked" label="Blokované" checked={blocked}
                onChange={(e) => update({ blokovane: e.target.checked ? '1' : null, strana: null })} />
            </CCol>
          </>
        )}
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}

      <CCard>
        {!result ? (
          <div className="p-4"><CSpinner color="primary" /></div>
        ) : result.items.length === 0 ? (
          <div className="p-4 text-body-secondary">
            {search || overdue || blocked ? 'Nic neodpovídá filtrům.' : archived ? 'Archiv je prázdný.' : 'Žádná rozpracovaná zakázka.'}
          </div>
        ) : (
          <CTable hover responsive className="mb-0">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Číslo</CTableHeaderCell>
                <CTableHeaderCell>Název</CTableHeaderCell>
                <CTableHeaderCell>Zákazník</CTableHeaderCell>
                <CTableHeaderCell>Zařízení</CTableHeaderCell>
                <CTableHeaderCell>Stav</CTableHeaderCell>
                {!archived && <CTableHeaderCell>Na tahu</CTableHeaderCell>}
                <CTableHeaderCell>Termín</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {result.items.map((c) => (
                <CTableRow key={c.id}>
                  <CTableDataCell className="text-nowrap"><Link to={`/zakazky/${c.id}`}>{c.contractNumber}</Link></CTableDataCell>
                  <CTableDataCell>
                    {c.title}
                    <span className="ms-2"><PriorityBadge priority={c.priority} /></span>
                    {c.isBlocked && <CBadge color="danger" className="ms-2">blokováno</CBadge>}
                  </CTableDataCell>
                  <CTableDataCell className="fw-semibold">{c.customer.name}</CTableDataCell>
                  <CTableDataCell className="small">
                    {c.items.map((i) => `${i.quantity}× ${i.deviceTypeName}`).join(', ')}
                  </CTableDataCell>
                  <CTableDataCell className="text-nowrap"><StateBadge state={c.state} /></CTableDataCell>
                  {!archived && (
                    <CTableDataCell className="small">
                      {c.nextStep && (c.nextStep.isMine
                        ? <span className="fw-semibold text-primary">vy — {c.nextStep.action.toLowerCase()}</span>
                        : <>{c.nextStep.waitingOn} <span className="text-body-secondary">— {c.nextStep.action.toLowerCase()}</span></>)}
                    </CTableDataCell>
                  )}
                  <CTableDataCell className={`text-nowrap ${c.isOverdue ? 'text-danger fw-semibold' : ''}`}>
                    {formatDate(c.dueDate)}
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        )}
      </CCard>

      {result && pages > 1 && (
        <CPagination className="mt-3">
          <CPaginationItem disabled={page <= 1} onClick={() => update({ strana: String(page - 1) })}>Předchozí</CPaginationItem>
          <CPaginationItem active>{page} / {pages}</CPaginationItem>
          <CPaginationItem disabled={page >= pages} onClick={() => update({ strana: String(page + 1) })}>Další</CPaginationItem>
        </CPagination>
      )}

      {result && <div className="text-body-secondary small mt-2">Celkem {result.total}</div>}
    </>
  );
}
